import api from 'api';
import classNames from 'classnames';
import BareHeading from 'components/Headings/BareHeading';
import notificationUtils from 'components/Modal/NotificationsModal/utils';
import Table from 'components/Tables/Table';
import TableHeader from 'components/Tables/Table/components/TableHeader';
import useTable from 'hooks/useTable';
import React, { useCallback, useEffect, useMemo } from 'react';
import socketKeys from 'utilities/enums/socketKeys';
import SignalRService from 'utilities/services/SignalRService';
import TableHeaders from 'utilities/services/TableHeaders';
import NotificationsTableRow from './components/NotificationsTableRow';
import './styles.scss';

const { NOTIFICATION_STATUSES } = notificationUtils;

const Notifications = ({ className }) => {
  const classes = classNames('ickyc-page', 'ickyc-notifications', className);
  const {
    isLoading,
    records,
    changeParams,
    pagination,
    params,
    removeRow,
    updateRowData,
    addRow,
    setRecords,
  } = useTable(api.notifications.fetchNotifications, { limit: 10, sortCriteria: 'dateUpdated', includeArchived: true });

  const tableRowSocketUpdate = useCallback(
    ({ id, ...rest }) => {
      if (records.length) {
        if (records.some(record => record.id === id)) {
          updateRowData('id', id, {
            ...rest,
          });
        } else {
          addRow({
            ...rest,
            id,
          });
        }
      }
    },
    [updateRowData, records],
  );

  const tableRowSocketUpdateWithoutItems = useCallback(
    ({ id, ...rest }) => {
      if (records.length) {
        const alteredRow = records.find(record => record.id === id);
        if (alteredRow) {
          updateRowData('id', id, {
            ...rest,
            items: alteredRow?.items,
          });
        } else {
          addRow({
            ...rest,
            id,
          });
        }
      }
    },
    [updateRowData, records],
  );

  const setAllStatusesToRead = useCallback(() => {
    setRecords(prevRecords => prevRecords.map(record => ({ ...record, status: NOTIFICATION_STATUSES.READ })));
  }, [setRecords]);

  const listenToAllNotificationsEvents = keyCallbackPairs => {
    Object.entries(keyCallbackPairs).forEach(([key, callback]) => {
      SignalRService.receiveMessage(key, callback);
    });
  };

  const keyCallbackPairs = useMemo(
    () => ({
      [socketKeys.ENTITY_ASSIGNED]: tableRowSocketUpdate,
      [socketKeys.CASE_ASSIGNED]: tableRowSocketUpdate,
      [socketKeys.ENTITY_CREATED]: tableRowSocketUpdate,
      [socketKeys.CASE_CREATED]: tableRowSocketUpdate,
      [socketKeys.RCU_RECEIVED]: tableRowSocketUpdate,
      [socketKeys.KYC_REFRESH_ARRIVED]: tableRowSocketUpdate,
      [socketKeys.ENTITY_ASSIGNED_NEW_ISSUE]: tableRowSocketUpdate,
      [socketKeys.CASE_ASSIGNED_OM_ISSUE]: tableRowSocketUpdate,
      [socketKeys.OM_EXPIRED]: tableRowSocketUpdate,
      [socketKeys.MENTION_IN_COMMENT]: tableRowSocketUpdate,
      [socketKeys.ENTITY_DELETED]: tableRowSocketUpdate,
      [socketKeys.CASE_DELETED]: tableRowSocketUpdate,
      [socketKeys.ENTITY_DELETION_FINISHED]: tableRowSocketUpdate,
      [socketKeys.CASE_DELETION_FINISHED]: tableRowSocketUpdate,
      [socketKeys.ALL_READ]: setAllStatusesToRead,
      [socketKeys.NOTIFICATION_STATUS]: tableRowSocketUpdateWithoutItems,
      [socketKeys.OM_EXPIRING]: tableRowSocketUpdate,
    }),
    [tableRowSocketUpdate],
  );

  useEffect(() => {
    listenToAllNotificationsEvents(keyCallbackPairs);
  }, [keyCallbackPairs]);

  return (
    <div className={classes}>
      <BareHeading title="Notifications" />
      <Table
        withLimitChange
        values={records}
        pagination={pagination}
        tableRow={NotificationsTableRow}
        onRemove={removeRow}
        withPagination
        updating={isLoading}
        handleParamsChange={changeParams}
        headerRow={TableHeader}
        headerData={{
          items: TableHeaders.NotificationsTableHeader,
        }}
        updateRowData={updateRowData}
        {...params}
      />
    </div>
  );
};
export default Notifications;
